import { useEffect, useMemo, useRef, useState } from 'react';
import { Search, X, Check, Briefcase, Loader2, AlertTriangle, ChevronDown } from 'lucide-react';
import { visitReportsApi } from '../../services/visitReportsApi';
import type { VisitReportDealsResponse, VisitReportDealOption } from '../../types/visitReport';

interface DealPickerProps {
    hubspotCompanyId: string;
    /** The currently attached deal, or null. Owned by the wizard, persisted in HubspotDealId. */
    value: string | null;
    onChange: (dealId: string | null, dealName: string | null) => void;
    disabled?: boolean;
}

/**
 * "Attach to a deal or project (optional)" — the picker in the review step.
 *
 * ## Why one picker and not two
 *
 * HubSpot has no project object. `Projects` (1088814) and `F&P Projects` (64290668) are deal
 * pipelines, verified against `HubSpot.DealPipeline`. Offering "deal" and "project" separately
 * would invent a distinction the CRM does not have; grouping by pipeline shows the real one.
 *
 * ## Why the whole list arrives at once
 *
 * Measured in P0 over all 31'746 deals: the largest company has 617 deals, 97.6 % have 25 or
 * fewer, no company spans more than 4 pipelines, and the most open deals on any one company is
 * 80. So there is no server-side search — one request, then the filter runs in the browser. The
 * server has already ordered the list (open pipelines first, open deals first inside each group);
 * this component does not re-sort it, because a second ordering is a second thing to drift.
 */
export function DealPicker({ hubspotCompanyId, value, onChange, disabled }: DealPickerProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [data, setData] = useState<VisitReportDealsResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // One fetch per company. The list is a nightly replica, so re-fetching on every open would
    // cost a round trip for data that cannot have changed within the session.
    useEffect(() => {
        if (!hubspotCompanyId) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        visitReportsApi.listDeals(hubspotCompanyId)
            .then(res => { if (!cancelled) setData(res); })
            .catch(err => { if (!cancelled) setError(err?.message || 'Could not load the deals'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [hubspotCompanyId]);

    useEffect(() => {
        if (!isOpen) return;
        const onDoc = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setIsOpen(false);
                setSearch('');
            }
        };
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { setIsOpen(false); setSearch(''); } };
        document.addEventListener('mousedown', onDoc);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDoc);
            document.removeEventListener('keydown', onKey);
        };
    }, [isOpen]);

    useEffect(() => { if (isOpen && inputRef.current) inputRef.current.focus(); }, [isOpen]);

    /** The selected deal, resolved out of the loaded list so the button can name it. */
    const selected: VisitReportDealOption | null = useMemo(() => {
        if (!value || !data) return null;
        for (const g of data.groups) {
            const hit = g.deals.find(d => d.id === value);
            if (hit) return hit;
        }
        return null;
    }, [value, data]);

    // Client-side filter over name and stage. Group order and deal order are the server's.
    const filteredGroups = useMemo(() => {
        if (!data) return [];
        const term = search.trim().toLowerCase();
        if (!term) return data.groups;
        return data.groups
            .map(g => ({
                ...g,
                deals: g.deals.filter(d =>
                    d.name.toLowerCase().includes(term)
                    || (d.stageLabel || '').toLowerCase().includes(term)
                    || g.pipelineLabel.toLowerCase().includes(term)),
            }))
            .filter(g => g.deals.length > 0);
    }, [data, search]);

    const pick = (deal: VisitReportDealOption | null) => {
        onChange(deal?.id ?? null, deal?.name ?? null);
        setIsOpen(false);
        setSearch('');
    };

    const hasDeals = Boolean(data && data.total > 0);

    return (
        <div className="rounded-xl border border-gray-200 bg-white p-3" ref={containerRef}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-gray-400" />
                    Attach to a deal or project <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                {data && hasDeals && (
                    <span className="text-xs text-gray-400">
                        {data.total} deal{data.total === 1 ? '' : 's'} · {data.openTotal} open
                    </span>
                )}
            </div>

            <div className="relative mt-2">
                <button
                    type="button"
                    onClick={() => setIsOpen(o => !o)}
                    disabled={disabled || loading || !hasDeals}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white text-left hover:bg-gray-50 disabled:bg-gray-50 disabled:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                    <span className="truncate">
                        {loading
                            ? 'Loading deals…'
                            : selected
                                ? selected.name
                                : value
                                    // The column holds an id the list does not contain — a deal
                                    // archived or re-assigned since the draft was saved. Naming the
                                    // raw id beats showing "No deal" over a link that still exists.
                                    ? `Deal ${value} (not in the current list)`
                                    : <span className="text-gray-400">No deal — the report goes to the company only</span>}
                    </span>
                    <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
                </button>

                {value && !disabled && (
                    <button
                        type="button"
                        onClick={() => pick(null)}
                        className="absolute right-8 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-100 text-gray-400"
                        aria-label="Detach the deal"
                        title="Detach the deal"
                    >
                        <X className="w-3.5 h-3.5" />
                    </button>
                )}

                {isOpen && (
                    <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b border-gray-100 p-2">
                            <div className="relative">
                                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    ref={inputRef}
                                    type="text"
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                    placeholder="Filter by deal, stage or pipeline…"
                                    className="w-full pl-8 pr-2 py-1.5 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={() => pick(null)}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50 border-b border-gray-100"
                        >
                            {!value && <Check className="w-4 h-4 text-blue-600 shrink-0" />}
                            <span className={!value ? 'text-blue-700' : 'text-gray-600'}>
                                No deal — company only
                            </span>
                        </button>

                        {filteredGroups.length === 0 && (
                            <div className="px-3 py-4 text-sm text-gray-500">
                                Nothing matches “{search}”.
                            </div>
                        )}

                        {filteredGroups.map(group => (
                            <div key={group.pipelineId}>
                                <div className="sticky top-[49px] flex items-center justify-between gap-2 px-3 py-1.5 bg-gray-50 border-y border-gray-100 text-xs font-semibold uppercase tracking-wide text-gray-500">
                                    <span className="truncate">{group.pipelineLabel}</span>
                                    <span className="font-normal normal-case text-gray-400 shrink-0">
                                        {group.openCount > 0 ? `${group.openCount} open` : 'all closed'}
                                    </span>
                                </div>
                                {group.deals.map(deal => (
                                    <button
                                        key={deal.id}
                                        type="button"
                                        onClick={() => pick(deal)}
                                        className="w-full flex items-start gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50"
                                    >
                                        <span className="w-4 shrink-0 pt-0.5">
                                            {value === deal.id && <Check className="w-4 h-4 text-blue-600" />}
                                        </span>
                                        <span className="min-w-0 flex-1">
                                            <span className={`block break-words ${value === deal.id ? 'text-blue-700 font-medium' : 'text-gray-800'}`}>
                                                {deal.name}
                                            </span>
                                            <span className="block text-xs text-gray-400">
                                                {deal.isClosed
                                                    ? <span className="text-gray-400">Closed</span>
                                                    : <span className="text-green-600 font-medium">Open</span>}
                                                {deal.stageLabel && <> · {deal.stageLabel}</>}
                                                {deal.closeDate && <> · {deal.closeDate}</>}
                                            </span>
                                        </span>
                                    </button>
                                ))}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {loading && (
                <p className="mt-1.5 flex items-center gap-1.5 text-xs text-gray-400">
                    <Loader2 className="w-3 h-3 animate-spin" /> Reading the deals…
                </p>
            )}

            {error && (
                <p className="mt-1.5 flex items-start gap-1.5 text-xs text-amber-800">
                    <AlertTriangle className="w-3.5 h-3.5 mt-px shrink-0" />
                    <span className="break-words">
                        {error} — the report can still be synced without a deal.
                    </span>
                </p>
            )}

            {!loading && !error && !hasDeals && (
                <p className="mt-1.5 text-xs text-gray-400">
                    This company has no deals, so the report goes to the company only.
                </p>
            )}

            {/* The list is a nightly replica, not a live read. Saying so is cheaper than a rep
                concluding the picker is broken because the deal they created an hour ago is
                missing — and the save path refuses such an id for the same reason. */}
            {!loading && !error && hasDeals && (
                <p className="mt-1.5 text-xs text-gray-400">
                    From the nightly HubSpot replica — a deal created in HubSpot today appears tomorrow.
                    {data?.truncated && ' Only the first 1000 deals are listed.'}
                </p>
            )}
        </div>
    );
}
